/**
 * Message Model - Data structure for multi-channel parent communications
 *
 * Swan's message delivery system supports multiple channels (Telegram, in-app, email, SMS)
 * with full tracking of delivery status, read receipts, and interactive responses.
 *
 * Design Principles:
 * - Channel-agnostic: Easy to add new delivery channels
 * - Two-way communication: Track both outbound and inbound interactions
 * - Per-channel tracking: Independent delivery status for each channel
 * - Interactive actions: Support action buttons (acknowledge, accept, schedule, etc.)
 *
 * @module models/Message
 */

/**
 * Message types aligned with Swan's discipline management domain
 * @typedef {('behavior_alert'|'weekly_summary'|'attendance_alert'|'achievement'|'consent_request'|'general_announcement')} MessageType
 */

/**
 * Message priority levels
 * @typedef {('low'|'normal'|'high'|'urgent')} MessagePriority
 */

/**
 * Message delivery status lifecycle:
 * - pending: Created but not yet queued for delivery
 * - queued: Waiting for delivery service to process
 * - sent: Delivery attempted (may not be received yet)
 * - delivered: Channel confirmed receipt (e.g., Telegram API confirmation)
 * - read: Parent opened/viewed the message
 * - responded: Parent clicked an action button
 * - expired: Message passed expiration date
 * - failed: Delivery failed after all retry attempts
 * @typedef {('pending'|'queued'|'sent'|'delivered'|'read'|'responded'|'expired'|'failed')} MessageStatus
 */

/**
 * Interactive action types
 * @typedef {('acknowledge'|'accept'|'decline'|'schedule'|'custom')} ActionType
 */

/**
 * Delivery channel identifiers
 * @typedef {('telegram'|'inApp'|'email'|'sms')} ChannelType
 */

/**
 * Message content structure
 * @typedef {Object} MessageContent
 * @property {string} subject - Message subject line (50-100 chars recommended)
 * @property {string} body - Full message text with details
 * @property {string} summary - Brief preview for notifications (50-100 chars)
 */

/**
 * Interactive action button
 * @typedef {Object} MessageAction
 * @property {string} id - Unique action identifier (e.g., 'action_1', 'acknowledge')
 * @property {ActionType} type - Action type
 * @property {string} label - Button label shown to parent (e.g., 'I Acknowledge')
 * @property {any} [value=null] - Pre-filled value if any
 */

/**
 * Response captured when parent clicks an action
 * @typedef {Object} ActionResponse
 * @property {string} actionId - Which action was clicked
 * @property {any} value - Response value (e.g., 'acknowledged', date selection, text input)
 * @property {import('firebase/firestore').Timestamp} timestamp - When parent responded
 */

/**
 * Per-channel delivery tracking
 * All channels are always present with enabled flag to avoid null checking
 * @typedef {Object} ChannelDelivery
 * @property {boolean} enabled - Whether this channel is available for the parent
 * @property {string} [messageId=null] - Platform-specific message ID (e.g., Telegram message ID)
 * @property {import('firebase/firestore').Timestamp} [sentAt=null] - When delivery was attempted
 * @property {import('firebase/firestore').Timestamp} [deliveredAt=null] - When channel confirmed receipt
 * @property {import('firebase/firestore').Timestamp} [readAt=null] - When parent opened message
 * @property {import('firebase/firestore').Timestamp} [respondedAt=null] - When parent clicked action
 * @property {ActionResponse} [response=null] - Parent's response if they clicked an action
 * @property {string} [error=null] - Last error message if delivery failed
 */

/**
 * Complete delivery tracking for all channels
 * @typedef {Object} MessageDelivery
 * @property {ChannelDelivery} telegram - Telegram bot delivery
 * @property {ChannelDelivery} inApp - In-app notification
 * @property {ChannelDelivery} email - Email delivery
 * @property {ChannelDelivery} sms - SMS delivery
 */

/**
 * Expected metadata structures by message type
 *
 * behavior_alert:
 *   { offenceTitle: string, offenceValue: number, mentionCount: number, consequenceIssued: boolean }
 *
 * weekly_summary:
 *   { weekStartDate: string, weekEndDate: string, totalMentions: number, totalAchievements: number, attendanceRate: number }
 *
 * attendance_alert:
 *   { date: string, status: 'absent'|'late', period: string }
 *
 * achievement:
 *   { achievementTitle: string, category: string, points: number }
 *
 * consent_request:
 *   { requestType: string, deadline: Date, documentUrl: string }
 *
 * general_announcement:
 *   { category: string, targetAudience: string }
 */

/**
 * Parent engagement metrics tracked in parentChildLinks collection
 *
 * These metrics are automatically updated by MessageService when messages
 * are sent, read, or responded to. Used for analytics and understanding
 * which communication channels parents prefer.
 *
 * engagementMetrics: {
 *   telegram: {
 *     messagesSent: number,      // Total messages sent via this channel
 *     messagesRead: number,       // Total messages read
 *     responsesReceived: number,  // Total action responses
 *     avgResponseTimeMs: number,  // Average time to respond (milliseconds)
 *     lastMessageAt: Timestamp    // Most recent message timestamp
 *   },
 *   inApp: {
 *     messagesSent: number,
 *     messagesRead: number,
 *     responsesReceived: number,
 *     avgResponseTimeMs: number,
 *     lastMessageAt: Timestamp
 *   },
 *   email: {
 *     messagesSent: number,
 *     messagesRead: number,
 *     responsesReceived: number,
 *     avgResponseTimeMs: number,
 *     lastMessageAt: Timestamp
 *   },
 *   sms: {
 *     messagesSent: number,
 *     messagesRead: number,
 *     responsesReceived: number,
 *     avgResponseTimeMs: number,
 *     lastMessageAt: Timestamp
 *   }
 * }
 */

/**
 * Complete message document structure
 * @typedef {Object} Message
 *
 * Identity & Classification
 * @property {string} id - Firestore auto-generated document ID
 * @property {string} schoolId - School identifier for multi-tenancy
 * @property {string} parentId - Parent user ID
 * @property {string} studentId - Student ID (REQUIRED: every message relates to a child)
 * @property {MessageType} type - Message type
 * @property {MessagePriority} priority - Message priority
 * @property {MessageStatus} status - Overall delivery status (highest status across channels)
 *
 * Message Content
 * @property {MessageContent} content - Message subject, body, and summary
 *
 * Interactive Actions
 * @property {MessageAction[]} [actions=[]] - Action buttons for parent interaction
 *
 * Multi-Channel Delivery
 * @property {MessageDelivery} delivery - Per-channel delivery tracking
 *
 * Metadata & Context
 * @property {string} [teacherId=null] - Teacher who triggered the message (for behavior alerts)
 * @property {string} [mentionId=null] - Mention/incident ID that triggered message (for behavior alerts)
 * @property {Object} [metadata={}] - Type-specific data (flexible structure, see metadata docs above)
 * @property {string} createdBy - User ID who created the message
 *
 * Timestamps
 * @property {import('firebase/firestore').Timestamp} createdAt - When message was created
 * @property {import('firebase/firestore').Timestamp} updatedAt - Last update timestamp
 * @property {import('firebase/firestore').Timestamp} [expiresAt=null] - Optional expiration date
 *
 * Computed Engagement Metrics
 * @property {number} readCount - Number of channels where message was read
 * @property {number} responseCount - Number of channels with parent response
 * @property {number} totalChannels - Number of enabled channels for this message
 */

/**
 * Create a new message document structure
 *
 * @param {Object} params - Message parameters
 * @param {string} params.schoolId - School identifier
 * @param {string} params.parentId - Parent user ID
 * @param {string} params.studentId - Student ID (required)
 * @param {MessageType} params.type - Message type
 * @param {MessagePriority} [params.priority='normal'] - Message priority
 * @param {MessageContent} params.content - Message content (subject, body, summary)
 * @param {MessageAction[]} [params.actions=[]] - Interactive action buttons
 * @param {Date} [params.expiresAt=null] - Optional expiration date
 * @param {string} [params.teacherId=null] - Teacher who triggered message
 * @param {string} [params.mentionId=null] - Related mention/incident ID
 * @param {Object} [params.metadata={}] - Type-specific metadata
 * @param {string} params.createdBy - User ID creating the message
 * @param {Object} params.enabledChannels - Which channels are enabled { telegram, inApp, email, sms }
 *
 * @returns {Omit<Message, 'id'>} Message document ready for Firestore (without ID)
 */
export function createMessageDocument({
  schoolId,
  parentId,
  studentId,
  type,
  priority = 'normal',
  content,
  actions = [],
  expiresAt = null,
  teacherId = null,
  mentionId = null,
  metadata = {},
  createdBy,
  enabledChannels
}) {
  // Validate required fields
  if (!schoolId || !parentId || !studentId || !type || !content || !createdBy) {
    throw new Error('Missing required message fields: schoolId, parentId, studentId, type, content, createdBy');
  }

  if (!content.subject || !content.body || !content.summary) {
    throw new Error('Message content must include subject, body, and summary');
  }

  // Create channel delivery structure with all channels always present
  const createChannelDelivery = (enabled) => ({
    enabled,
    messageId: null,
    sentAt: null,
    deliveredAt: null,
    readAt: null,
    respondedAt: null,
    response: null,
    error: null
  });

  const delivery = {
    telegram: createChannelDelivery(enabledChannels.telegram || false),
    inApp: createChannelDelivery(enabledChannels.inApp || false),
    email: createChannelDelivery(enabledChannels.email || false),
    sms: createChannelDelivery(enabledChannels.sms || false)
  };

  // Count enabled channels
  const totalChannels = Object.values(enabledChannels).filter(Boolean).length;

  // Convert expiresAt to Timestamp if provided
  const expiresAtTimestamp = expiresAt ?
    (typeof expiresAt.toDate === 'function' ? expiresAt : new Date(expiresAt)) :
    null;

  return {
    // Identity
    schoolId,
    parentId,
    studentId,

    // Classification
    type,
    priority,
    status: 'pending',

    // Content
    content: {
      subject: content.subject.trim(),
      body: content.body.trim(),
      summary: content.summary.trim()
    },

    // Actions
    actions,

    // Delivery tracking
    delivery,

    // Metadata
    teacherId,
    mentionId,
    metadata,
    createdBy,

    // Timestamps (serverTimestamp will be set by Firestore)
    createdAt: null, // Will be set by serverTimestamp()
    updatedAt: null, // Will be set by serverTimestamp()
    expiresAt: expiresAtTimestamp,

    // Engagement metrics
    readCount: 0,
    responseCount: 0,
    totalChannels
  };
}

/**
 * Create an action button for interactive messages
 *
 * @param {string} id - Unique action identifier
 * @param {ActionType} type - Action type
 * @param {string} label - Button label
 * @param {any} [value=null] - Pre-filled value
 * @returns {MessageAction}
 */
export function createAction(id, type, label, value = null) {
  return { id, type, label, value };
}

/**
 * Validate message type
 * @param {string} type - Message type to validate
 * @returns {boolean}
 */
export function isValidMessageType(type) {
  const validTypes = [
    'behavior_alert',
    'weekly_summary',
    'attendance_alert',
    'achievement',
    'consent_request',
    'general_announcement'
  ];
  return validTypes.includes(type);
}

/**
 * Validate message status
 * @param {string} status - Status to validate
 * @returns {boolean}
 */
export function isValidMessageStatus(status) {
  const validStatuses = [
    'pending',
    'queued',
    'sent',
    'delivered',
    'read',
    'responded',
    'expired',
    'failed'
  ];
  return validStatuses.includes(status);
}

/**
 * Get the highest status across all enabled channels
 * Used to compute overall message status
 *
 * Status hierarchy (lowest to highest):
 * pending < queued < sent < delivered < read < responded
 *
 * @param {MessageDelivery} delivery - Delivery tracking object
 * @returns {MessageStatus}
 */
export function computeOverallStatus(delivery) {
  const statusHierarchy = {
    'pending': 0,
    'queued': 1,
    'sent': 2,
    'delivered': 3,
    'read': 4,
    'responded': 5,
    'expired': 6,
    'failed': 7
  };

  let highestStatus = 'pending';
  let highestValue = 0;

  // Check each enabled channel
  Object.values(delivery).forEach(channel => {
    if (!channel.enabled) return;

    let channelStatus = 'pending';

    if (channel.respondedAt) {
      channelStatus = 'responded';
    } else if (channel.readAt) {
      channelStatus = 'read';
    } else if (channel.deliveredAt) {
      channelStatus = 'delivered';
    } else if (channel.sentAt && !channel.error) {
      channelStatus = 'sent';
    } else if (channel.error) {
      channelStatus = 'failed';
    } else if (channel.sentAt) {
      // Sent but has error - mark as failed
      channelStatus = 'failed';
    }

    const channelValue = statusHierarchy[channelStatus];
    if (channelValue > highestValue) {
      highestValue = channelValue;
      highestStatus = channelStatus;
    }
  });

  return highestStatus;
}

/**
 * Check if message has expired
 * @param {Message} message - Message document
 * @returns {boolean}
 */
export function isExpired(message) {
  if (!message.expiresAt) return false;

  const now = new Date();
  const expiresAt = message.expiresAt.toDate ? message.expiresAt.toDate() : new Date(message.expiresAt);

  return now > expiresAt;
}

/**
 * Constants for message types
 */
export const MESSAGE_TYPES = {
  BEHAVIOR_ALERT: 'behavior_alert',
  WEEKLY_SUMMARY: 'weekly_summary',
  ATTENDANCE_ALERT: 'attendance_alert',
  ACHIEVEMENT: 'achievement',
  CONSENT_REQUEST: 'consent_request',
  GENERAL_ANNOUNCEMENT: 'general_announcement'
};

/**
 * Constants for message priorities
 */
export const MESSAGE_PRIORITIES = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent'
};

/**
 * Constants for message statuses
 */
export const MESSAGE_STATUSES = {
  PENDING: 'pending',
  QUEUED: 'queued',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  RESPONDED: 'responded',
  EXPIRED: 'expired',
  FAILED: 'failed'
};

/**
 * Constants for action types
 */
export const ACTION_TYPES = {
  ACKNOWLEDGE: 'acknowledge',
  ACCEPT: 'accept',
  DECLINE: 'decline',
  SCHEDULE: 'schedule',
  CUSTOM: 'custom'
};

/**
 * Constants for delivery channels
 */
export const CHANNELS = {
  TELEGRAM: 'telegram',
  IN_APP: 'inApp',
  EMAIL: 'email',
  SMS: 'sms'
};

export default {
  createMessageDocument,
  createAction,
  isValidMessageType,
  isValidMessageStatus,
  computeOverallStatus,
  isExpired,
  MESSAGE_TYPES,
  MESSAGE_PRIORITIES,
  MESSAGE_STATUSES,
  ACTION_TYPES,
  CHANNELS
};
